import { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { Calendar, Plus, Lock, Unlock } from 'lucide-react';
import { api } from '../../lib/api';

export function FiscalPeriodsPage() {
  const queryClient = useQueryClient();
  const [showAddModal, setShowAddModal] = useState(false);
  const [formData, setFormData] = useState({
    periodName: '',
    startDate: '',
    endDate: '',
  });

  const { data, isLoading } = useQuery({
    queryKey: ['fiscal-periods'],
    queryFn: () => api.get<{ data: any[] }>('/api/tax/fiscal-periods'),
  });

  const addMutation = useMutation({
    mutationFn: (data: any) => api.post('/api/tax/fiscal-periods', data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['fiscal-periods'] });
      setShowAddModal(false);
      setFormData({ periodName: '', startDate: '', endDate: '' });
    },
  });

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    addMutation.mutate(formData);
  };

  const statusLabels: Record<string, { label: string; class: string }> = {
    open: { label: '進行中', class: 'badge-success' },
    closed: { label: '締切済', class: 'badge-warning' },
    locked: { label: 'ロック', class: 'badge-error' },
  };

  return (
    <div className="space-y-6 animate-fadeIn">
      <div className="flex items-center justify-between">
        <h1 className="text-2xl font-bold" style={{ color: 'var(--text-primary)' }}>
          会計期間管理
        </h1>
        <button
          onClick={() => setShowAddModal(true)}
          className="flex items-center gap-2 px-4 py-2 rounded-lg text-white text-sm"
          style={{ backgroundColor: 'var(--accent-color)' }}
        >
          <Plus size={16} />
          新規期間作成
        </button>
      </div>

      <div className="card overflow-hidden">
        {isLoading ? (
          <div className="flex items-center justify-center h-64">
            <div className="w-8 h-8 border-2 border-blue-600/30 border-t-blue-600 rounded-full animate-spin" />
          </div>
        ) : data?.data.length === 0 ? (
          <div className="text-center py-12">
            <Calendar size={48} className="mx-auto mb-4" style={{ color: 'var(--text-muted)' }} />
            <p style={{ color: 'var(--text-secondary)' }}>会計期間が登録されていません</p>
          </div>
        ) : (
          <table>
            <thead>
              <tr>
                <th style={{ color: 'var(--text-primary)' }}>期間名</th>
                <th style={{ color: 'var(--text-primary)' }}>開始日</th>
                <th style={{ color: 'var(--text-primary)' }}>終了日</th>
                <th style={{ color: 'var(--text-primary)' }}>ステータス</th>
                <th style={{ color: 'var(--text-primary)' }}>操作</th>
              </tr>
            </thead>
            <tbody>
              {data?.data.map((period: any) => (
                <tr key={period.id}>
                  <td className="font-medium" style={{ color: 'var(--text-primary)' }}>{period.periodName}</td>
                  <td style={{ color: 'var(--text-secondary)' }}>{period.startDate}</td>
                  <td style={{ color: 'var(--text-secondary)' }}>{period.endDate}</td>
                  <td>
                    <span className={`badge ${statusLabels[period.status]?.class || 'badge-info'}`}>
                      {statusLabels[period.status]?.label || period.status}
                    </span>
                  </td>
                  <td>
                    <div className="flex items-center gap-2">
                      {period.status === 'open' && (
                        <button
                          className="p-1.5 rounded hover:bg-orange-50 dark:hover:bg-orange-900/20 text-orange-600"
                          title="締め切り"
                        >
                          <Lock size={16} />
                        </button>
                      )}
                      {period.status === 'closed' && (
                        <button
                          className="p-1.5 rounded hover:bg-green-50 dark:hover:bg-green-900/20 text-green-600"
                          title="再オープン"
                        >
                          <Unlock size={16} />
                        </button>
                      )}
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>

      {/* 期間追加モーダル */}
      {showAddModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
          <div className="card p-6 w-full max-w-md animate-slideIn">
            <h2 className="text-xl font-bold mb-4" style={{ color: 'var(--text-primary)' }}>会計期間作成</h2>
            <form onSubmit={handleSubmit} className="space-y-4">
              <div>
                <label className="block text-sm font-medium mb-1" style={{ color: 'var(--text-primary)' }}>期間名 *</label>
                <input
                  type="text"
                  value={formData.periodName}
                  onChange={(e) => setFormData({ ...formData, periodName: e.target.value })}
                  required
                  className="w-full px-3 py-2 rounded-lg border text-sm"
                  style={{ backgroundColor: 'var(--input-bg)', borderColor: 'var(--input-border)', color: 'var(--text-primary)' }}
                  placeholder="第10期（2024年4月〜2025年3月）"
                />
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium mb-1" style={{ color: 'var(--text-primary)' }}>開始日 *</label>
                  <input
                    type="date"
                    value={formData.startDate}
                    onChange={(e) => setFormData({ ...formData, startDate: e.target.value })}
                    required
                    className="w-full px-3 py-2 rounded-lg border text-sm"
                    style={{ backgroundColor: 'var(--input-bg)', borderColor: 'var(--input-border)', color: 'var(--text-primary)' }}
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium mb-1" style={{ color: 'var(--text-primary)' }}>終了日 *</label>
                  <input
                    type="date"
                    value={formData.endDate}
                    onChange={(e) => setFormData({ ...formData, endDate: e.target.value })}
                    required
                    className="w-full px-3 py-2 rounded-lg border text-sm"
                    style={{ backgroundColor: 'var(--input-bg)', borderColor: 'var(--input-border)', color: 'var(--text-primary)' }}
                  />
                </div>
              </div>
              <div className="flex justify-end gap-2 mt-6">
                <button
                  type="button"
                  onClick={() => setShowAddModal(false)}
                  className="px-4 py-2 rounded-lg border text-sm"
                  style={{ borderColor: 'var(--border-color)', color: 'var(--text-primary)' }}
                >
                  キャンセル
                </button>
                <button
                  type="submit"
                  disabled={addMutation.isPending}
                  className="px-4 py-2 rounded-lg text-white text-sm disabled:opacity-50"
                  style={{ backgroundColor: 'var(--accent-color)' }}
                >
                  {addMutation.isPending ? '作成中...' : '作成'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
}

